import React, { useState, useEffect } from "react";
import { useLocation, Redirect } from "react-router-dom";
import ConfirmedActions from "../../Components/ConfirmedActions/ConfirmedActions";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Card, CardContent, Grid, Tooltip } from "@material-ui/core";
import { ACCOUNT_COPY, trimAccountFields } from "../../Services/API/UserAccountActions";
import {
  useAccountStatusQuery,
  useActivateAccountMutation,
  useDeactivateAccountMutation,
  useDeleteAccountMutation,
  useEditAccountMutation,
  useResendInviteMutation,
  useResetPasswordForUserMutation,
} from "../../Queries/UserManagementQueries";
import { StyledTextField } from "../../Components/TextField/TextField";

function UserManagementDetailPage() {
  const useStyles = makeStyles((theme) => ({
    root: {
      marginBottom: "50px",
      [theme.breakpoints.down("sm")]: {
        maxWidth: "unset",
        width: "100%",
      },
      height: "90vh",
      maxWidth: "2400px",
    },
    error: {
      color: theme.palette.error.main,
    },
    textField: {
      "& .Mui-error": {
        color: "rgba(255,0,0,.7)",
      },
      "& .MuiFormHelperText-root": {
        color: "rgba(255,0,0,.7)",
      },
      "& .MuiInput-underline.Mui-error:after": {
        borderBottomColor: "rgba(255,0,0,.7) !important",
      },
    },
    cardContainer: {
      [theme.breakpoints.down("sm")]: {
        maxWidth: "none",
        width: "100%",
        height: "85%",
        overflowY: "scroll",
      },
      width: "85%",
      height: "90%",
      display: "flex",
      flexDirection: "column",
      overflowX: "none",
    },
    card: {
      overflow: "unset",
    },
    sensitiveSection: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      paddingTop: "20px",
    },
    resendWrapper: {
      [theme.breakpoints.down("sm")]: {
        display: "block",
        marginRight: "0px",
      },
      display: "inline-block",
      marginRight: "20px",
    },
    sensitiveButton: {
      [theme.breakpoints.down("sm")]: {
        maxWidth: "none",
        width: "100%",
        marginBottom: "15px",
      },
      width: "200px",
    },
    header: {
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        marginTop: "10px",
      },
      display: "flex",
    },
    descLabel: {
      margin: "0px",
      fontWeight: "700",
      marginBottom: "10px",
    },
    descValue: {
      margin: "0px",
      marginLeft: "5px",
      marginRight: "20px",
    },
    descBlock: {
      display: "flex",
    },
    invitePending: {
      margin: "0px",
      fontWeight: "700",
      color: theme.palette.warning.main,
    },
  }));

  const location = useLocation();

  const [dateCreated, setDateCreated] = useState("");
  const [dateUpdated, setDateUpdated] = useState("");
  const [inputError, setInputError] = useState(false);
  const [firstName, setFirstName] = useState(location.state === undefined ? "" : location.state.firstName);
  const [lastName, setLastName] = useState(location.state === undefined ? "" : location.state.lastName);
  const [email, setEmail] = useState(location.state === undefined ? "" : location.state.emailAddress);
  const [phone, setPhone] = useState(location.state === undefined ? "" : location.state.phoneNumber);
  const [isActive, setIsActive] = useState(location.state === undefined ? false : location.state.isActive);
  const [deleted, setDeleted] = useState(false);

  const classes = useStyles();
  const { data: accountStatus } = useAccountStatusQuery(location.state?.id);
  const { mutateAsync: editAccount } = useEditAccountMutation();
  const { mutateAsync: activateAccount } = useActivateAccountMutation();
  const { mutateAsync: deactivateAccount } = useDeactivateAccountMutation();
  const { mutateAsync: deleteAccount } = useDeleteAccountMutation();
  const { mutateAsync: resendInvite } = useResendInviteMutation();
  const { mutateAsync: resetPasswordForUser } = useResetPasswordForUserMutation();

  const currentFields = () =>
    trimAccountFields({
      FirstName: firstName,
      LastName: lastName,
      EmailAddress: email,
      PhoneNumber: phone,
    });

  const checkInput = (fields) => {
    if (
      fields.FirstName === "" ||
      fields.LastName === "" ||
      fields.EmailAddress === "" ||
      (fields.PhoneNumber.length > 0 && fields.PhoneNumber.length < 7)
    ) {
      setInputError(true);
      return false;
    }
    return true;
  };

  const saveInformation = (start) => {
    const fields = currentFields();
    setFirstName(fields.FirstName);
    setLastName(fields.LastName);
    setEmail(fields.EmailAddress);
    if (checkInput(fields)) {
      start("save");
    }
  };

  useEffect(() => {
    if (location.state !== undefined) {
      const createDate = new Date(location.state.dateCreated).toLocaleDateString("en-US");
      setDateCreated(createDate);
      const updateDate = new Date(location.state.dateLastUpdated).toLocaleDateString("en-US");
      setDateUpdated(updateDate);
    }
  }, [location.state]);

  if (location.state === undefined) {
    return <Redirect to="/usermanagement" />;
  }

  if (deleted) {
    return <Redirect to={{ pathname: "/usermanagement", state: { message: ACCOUNT_COPY.deleted } }} />;
  }

  const userId = location.state.id;
  const actions = [
    {
      key: "save",
      run: () => editAccount({ userId, fields: currentFields() }),
    },
    {
      key: "reset",
      question: "Send this account an email to reset password?",
      run: () => resetPasswordForUser(userId),
    },
    {
      key: "toggleActive",
      question: isActive ? "Block NAS access to this account?" : "Unblock NAS access to this account?",
      run: () => (isActive ? deactivateAccount(userId) : activateAccount(userId)),
      onSuccess: () => setIsActive(!isActive),
    },
    {
      key: "delete",
      question: "Delete this account permanently?",
      run: () => deleteAccount(userId),
      onSuccess: () => setDeleted(true),
    },
    {
      key: "resendInvite",
      question: ACCOUNT_COPY.resendConfirm,
      run: () => resendInvite(userId),
    },
  ];

  return (
    <ConfirmedActions actions={actions}>
      {(start, busy) => (
        <>
          <div className={classes.header}>
            <div className={classes.descBlock}>
              <p className={classes.descLabel}>DATE CREATED: </p>
              <p className={classes.descValue}>{dateCreated}</p>
            </div>
            <div className={classes.descBlock}>
              <p className={classes.descLabel}>DATE LAST UPDATED: </p>
              <p className={classes.descValue}>{dateUpdated}</p>
            </div>
            {accountStatus?.ok && !accountStatus.hasLoggedIn ? (
              <p className={classes.invitePending} data-test="user-invite-pending">
                {ACCOUNT_COPY.invitePending}
              </p>
            ) : null}
          </div>
          <div className={classes.root}>
            <Grid className={classes.cardContainer}>
              <Card className={classes.card}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid xs={12} sm={6} item>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        className={classes.textField}
                        placeholder="First name"
                        label="First Name"
                        variant="standard"
                        fullWidth
                        required
                        inputProps={{ "data-test": "user-first-name" }}
                        value={firstName}
                        error={inputError && firstName === ""}
                        helperText={inputError && firstName === "" ? "First Name Required" : ""}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </Grid>
                    <Grid xs={12} sm={6} item>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        className={classes.textField}
                        placeholder="Last Name"
                        label="Last Name"
                        variant="standard"
                        fullWidth
                        required
                        inputProps={{ "data-test": "user-last-name" }}
                        value={lastName}
                        error={inputError && lastName === ""}
                        helperText={inputError && lastName === "" ? "Last Name Required" : ""}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        className={classes.textField}
                        type="email"
                        placeholder="Email Address"
                        label="Email Address"
                        variant="standard"
                        fullWidth
                        required
                        inputProps={{ "data-test": "user-email" }}
                        value={email}
                        error={inputError && email === ""}
                        helperText={inputError && email === "" ? "Email Address Required" : ""}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        className={classes.textField}
                        placeholder="Phone Number"
                        label="Phone Number"
                        variant="standard"
                        fullWidth
                        value={phone}
                        error={inputError && phone.length > 0 && phone.length < 7}
                        helperText={
                          inputError && phone.length > 0 && phone.length < 7
                            ? "Invalid Cell Phone Number"
                            : ""
                        }
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} container justifyContent="flex-end">
                      <Button
                        style={{ marginTop: "20px", marginRight: "8px" }}
                        className={classes.sensitiveButton}
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={busy}
                        onClick={() => saveInformation(start)}
                      >
                        Save Information
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <div className={classes.sensitiveSection}>
                <Grid item xs={12} sm={12} container justifyContent="space-between">
                  <Button
                    className={classes.sensitiveButton}
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={busy}
                    onClick={() => start("delete")}
                  >
                    Delete Account
                  </Button>
                  <div>
                    {/* A disabled button fires no mouse events, so the span carries the tooltip. */}
                    <Tooltip title={isActive ? "" : ACCOUNT_COPY.resendInactive}>
                      <span className={classes.resendWrapper}>
                        <Button
                          style={{ backgroundColor: "rgba(191,199,114,.8)" }}
                          className={classes.sensitiveButton}
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={busy || !isActive}
                          data-test="user-resend-invite-button"
                          onClick={() => start("resendInvite")}
                        >
                          Resend Invite
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      style={{
                        backgroundColor: "rgba(191,199,114,.8)",
                        marginRight: "20px",
                      }}
                      className={classes.sensitiveButton}
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={() => start("reset")}
                    >
                      Reset Password
                    </Button>
                    <Button
                      style={{ backgroundColor: "rgba(191,199,114,.8)" }}
                      className={classes.sensitiveButton}
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={() => start("toggleActive")}
                    >
                      {isActive ? "Deactivate Account" : "Activate Account"}
                    </Button>
                  </div>
                </Grid>
              </div>
            </Grid>
          </div>
        </>
      )}
    </ConfirmedActions>
  );
}

export default UserManagementDetailPage;
